import React, { useState, useEffect } from 'react';
import {
  params,
  extend,
  string as stringType,
  useLazyDataModel,
  shape as shapeType,
  arrayOf
} from '@thd-nucleus/data-sources';
import proptypes from 'prop-types';
import {
  FormController,
  FormLabel,
  Dropdown,
  MenuItem,
  TextField
} from '@one-thd/sui-atomic-components';
import {
  isZipRejectedByLookup,
  validateField
} from '../helpers';
import {
  EMPTY_ZIP_CODE_MESSAGE,
  EMPTY_ADDRESS_MESSAGE,
  INVALID_ZIP_CODE,
  ADDRESS_LINE_1_KEY
} from '../constants';

const { bool, func, string, shape } = proptypes;

// The pre-DSCVR-5168 field labels, which Project Setup and the switched-off create drawer keep, and
// the switched-on create drawer's. Chosen once per render rather than a ternary per field.
// [DSCVR-5191]
const LABELS = { cityState: 'City, State', zipCode: 'Zip Code' };
const JOBSITE_LABELS = { cityState: 'City/State', zipCode: 'Delivery Zip Code' };

export const JobsiteInformationForm = ({
  showErrors,
  jobsiteInformation,
  setJobsiteInformation,
  isJobsiteError,
  setIsJobsiteError,
  open,
  isZipOnlyEnabled,
  isJobsiteInformationEnabled
}) => {

  const [errors, setErrors] = useState({
    addressLine1Error: '',
    addressLine2Error: '',
    zipCodeError: ''
  });

  const {
    addressLine1Error,
    addressLine2Error,
    zipCodeError
  } = errors;

  // Safely access jobsite information with default values
  const {
    addressLine1 = '',
    addressLine2 = '',
    zipCode = '',
    city = '',
    state = ''
  } = jobsiteInformation || {};

  const [dropdownOptions, setDropdownOptions] = useState([]);
  const [dropdownSelection, setDropdownSelection] = useState('');
  const [placeholderValue, setPlaceholderValue] = useState('');
  const handleDropdownSelection = (event) => {
    setDropdownSelection(event.target.value);
  };

  const [
    fetchCityState, cityAndStateResponse
  ] = useLazyDataModel('cityStateLookupByZipCode', {
    variables: {},
    skip: (zipCode?.length !== 5),
    context: { withAuth: true },
    fetchPolicy: 'cache-first'
  });

  useEffect(() => {
    if (zipCode?.length === 5) {
      fetchCityState({
        variables: {
          zipCode
        }
      });
    }
  }, [zipCode]);

  const getDropdownValue = () => {
    const sanitizedState = state?.trim();
    const cityAndState = `${city}, ${sanitizedState}`;
    const index = dropdownOptions.indexOf(cityAndState);
    const value = dropdownOptions[index];
    if (dropdownSelection?.length && index >= 0) {
      if (dropdownSelection !== placeholderValue) {
        setPlaceholderValue(dropdownSelection);
      }
      return dropdownSelection;
    }
    if (city && sanitizedState && dropdownOptions.length > 0) {
      if (value !== placeholderValue) {
        setPlaceholderValue(value);
      }
      return value;
    }
    if (value?.length && placeholderValue !== value) {
      setPlaceholderValue(value);
    } else if (placeholderValue !== dropdownOptions[0]) {
      setPlaceholderValue(dropdownOptions[0]);
    }
    return dropdownOptions[0] || '';

  };

  const cityAndStateData = cityAndStateResponse?.data;
  const cityAndStateError = cityAndStateResponse?.error;
  // Switched on (DSCVR-5168), a lookup rejection is derived on every render rather than stored in
  // errors: the address-line effect below rewrites zipCodeError whenever the zip changes, so a
  // rejection kept there was wiped on edit and never set again for a retyped zip answered from
  // the cache. Switched off, the pre-5168 handling in the effect below applies. [DSCVR-5191]
  const isZipNotFound = isJobsiteInformationEnabled && isZipRejectedByLookup(zipCode, cityAndStateResponse);
  const zipCodeMessage = zipCodeError || (isZipNotFound ? INVALID_ZIP_CODE : '');

  useEffect(() => {
    const cityAndStateArray = cityAndStateData?.cityStateLookupByZipCode?.postalDetailsList;
    if (cityAndStateArray) {
      // Build the list once instead of resetting and appending per result. React 16 batches
      // these into a single commit either way, so this is not a render saving - it matters
      // because under React 18's createRoot the reset-then-append loop would expose partial
      // arrays to the [dropdownOptions] effect below mid-build.
      setDropdownOptions(
        cityAndStateArray.map((location) => `${location.city}, ${location.state}`)
      );
      if (!isJobsiteInformationEnabled) {
        setErrors((prevState) => ({
          ...prevState,
          zipCodeError: ''
        }));
      }
    }
    if (!isJobsiteInformationEnabled && cityAndStateError?.message) {
      setErrors((prevState) => ({
        ...prevState,
        zipCodeError: INVALID_ZIP_CODE
      }));
    }
  }, [cityAndStateData]);

  useEffect(() => {
    const sanitizedState = state?.trim();
    const cityAndState = `${city}, ${sanitizedState}`;
    const index = dropdownOptions?.indexOf(cityAndState);
    if (index < 0) {
      setPlaceholderValue(dropdownOptions[0] || '');
      setDropdownSelection(dropdownOptions[0] || '');
    } else {
      setPlaceholderValue(dropdownOptions[index]);
    }
    getDropdownValue();
  }, [dropdownOptions]);

  useEffect(() => {
    if (dropdownSelection?.length) {
      // The option is built as `${city}, ${state}`, so splitting on the comma leaves the state
      // with a leading space. It used to only ever be compared (against a trimmed copy) or sent
      // alongside a street address; now that a streetless jobsite sends city and state to
      // createProject (DSCVR-5168), the stored value has to be clean at the source.
      // Switched off, the values are stored as they always were. [DSCVR-5191]
      const [newCity, newState] = dropdownSelection.split(',');
      setJobsiteInformation((prevState) => ({
        ...prevState,
        city: isJobsiteInformationEnabled ? newCity?.trim() : newCity,
        state: isJobsiteInformationEnabled ? newState?.trim() : newState
      }));
    }
  }, [dropdownSelection]);

  const handleAddressOneValidation = (value) => {
    const { isError, errorMessage } = validateField({ value, key: ADDRESS_LINE_1_KEY });
    if (isError) {
      setErrors((prevState) => ({
        ...prevState,
        addressLine1Error: errorMessage
      }));
    } else if (!isError && addressLine1Error !== EMPTY_ADDRESS_MESSAGE) {
      setErrors((prevState) => ({
        ...prevState,
        addressLine1Error: ''
      }));
    }
  };

  const handleAddressTwoValidation = (value) => {
    const { isError, errorMessage } = validateField({ value, key: ADDRESS_LINE_1_KEY });
    if (isError) {
      setErrors((prevState) => ({
        ...prevState,
        addressLine2Error: errorMessage
      }));
    } else {
      setErrors((prevState) => ({
        ...prevState,
        addressLine2Error: ''
      }));
    }
  };

  const handleOnChange = (key, event) => {
    const { value } = event.target;
    setJobsiteInformation((prevState) => ({
      ...prevState,
      [key]: value
    }));
  };

  useEffect(() => {
    const errorMessageValues = Object.values(errors);
    const hasErrors = errorMessageValues.some(Boolean) || isZipNotFound;
    if (hasErrors) {
      setIsJobsiteError(true);
    } else {
      setIsJobsiteError(false);
    }
  }, [errors, isZipNotFound]);

  const [isOptional, setIsOptional] = useState(true);
  useEffect(() => {
    if (!isZipOnlyEnabled) {
      // Pre-DSCVR-4956: touching any one of the three fields makes both the address line and the
      // zip required, so a zip on its own is an incomplete entry and blocks submit.
      const hasAnyField = Boolean(addressLine1 || addressLine2 || zipCode);
      setIsOptional(!hasAnyField);
      setErrors((prevState) => ({
        ...prevState,
        addressLine1Error: (hasAnyField && !addressLine1) ? EMPTY_ADDRESS_MESSAGE : '',
        zipCodeError: (hasAnyField && !zipCode) ? EMPTY_ZIP_CODE_MESSAGE : ''
      }));
      return;
    }
    // A zip code on its own is a complete jobsite entry — it is enough to drive inventory and
    // delivery estimates without a street address. So the address pair only becomes required
    // once the customer starts entering an address line: an address line with no zip is still
    // an error, but a zip with no address line is not.
    const hasAddressLine = Boolean(addressLine1 || addressLine2);
    setIsOptional(!hasAddressLine);
    setErrors((prevState) => ({
      ...prevState,
      addressLine1Error: (hasAddressLine && !addressLine1) ? EMPTY_ADDRESS_MESSAGE : '',
      zipCodeError: (hasAddressLine && !zipCode) ? EMPTY_ZIP_CODE_MESSAGE : ''
    }));
  }, [addressLine1, addressLine2, zipCode, isZipOnlyEnabled]);

  useEffect(() => {
    if (!open && isJobsiteError) {
      setErrors((prevState) => ({
        ...prevState,
        addressLine1Error: '',
        addressLine2Error: '',
        cityError: '',
        stateError: '',
        zipCodeError: ''
      }));
    }
  }, [open]);

  // Once the top-level Delivery Zip Code is enabled this field is the exact same value, already
  // required there - showing it as "(optional)" here would be misleading, and the comp marks it "*"
  // unconditionally, unlike the address lines. Switched off it is the pre-5168 "Zip Code" field,
  // which is what Project Setup shows. [DSCVR-5168, DSCVR-5191]
  const labels = isJobsiteInformationEnabled ? JOBSITE_LABELS : LABELS;
  const zipCodeField = (
    <TextField
      fullWidth
      // undefined, not false, when switched off: the pre-5168 field never passed `required`.
      required={isJobsiteInformationEnabled || undefined}
      optional={isJobsiteInformationEnabled ? false : isOptional}
      value={zipCode || ''}
      id="jobsite-zipcode"
      label={labels.zipCode}
      name="zipCode"
      onChange={(event) => { handleOnChange('zipCode', event); }}
      status={
        showErrors && zipCodeMessage ? 'error' : null
      }
      statusMessage={
        showErrors ? zipCodeMessage : ''
      }
      InputProps={{
        inputProps: {
          'data-testid': 'jobsite-zipcode-input',
          maxLength: '5'
        }
      }}
    />
  );

  return (
    <div className="sui-grid sui-gap-6">
      <TextField
        fullWidth
        optional={isOptional}
        value={addressLine1 || ''}
        id="jobsite-address-one"
        label="Address Line 1"
        name="addressLine1"
        onBlur={() => { handleAddressOneValidation(addressLine1); }}
        onChange={(event) => { handleOnChange('addressLine1', event); }}
        status={
          showErrors && addressLine1Error ? 'error' : null
        }
        statusMessage={
          showErrors ? addressLine1Error : ''
        }
        InputProps={{
          inputProps: {
            'data-testid': 'jobsite-address-one-input'
          }
        }}
      />
      <TextField
        fullWidth
        optional
        value={addressLine2 || ''}
        id="jobsite-address-two"
        label="Address Line 2"
        name="addressLine2"
        onBlur={() => { handleAddressTwoValidation(addressLine2); }}
        onChange={(event) => { handleOnChange('addressLine2', event); }}
        status={
          showErrors && addressLine2Error ? 'error' : null
        }
        statusMessage={
          showErrors ? addressLine2Error : ''
        }
        InputProps={{
          inputProps: {
            'data-testid': 'jobsite-address-two-input'
          }
        }}
      />
      {/* DSCVR-5168: the comp orders Address Details as Street Address, City/State, then
          Delivery Zip Code - the zip is last, after the lookup has a city/state to show.
          Address Line 1/2 keep their original naming and stay unconditionally visible - not
          matched to the comp here per product decision (no AC covers removing them).
          Switched off, the pre-5168 order and labels apply. [DSCVR-5191] */}
      {!isJobsiteInformationEnabled && zipCodeField}
      <FormController fullWidth>
        <FormLabel
          optional
          htmlFor="jobsite-information-dropdown"
          id="jobsite-information-dropdown-label"
        >
          {labels.cityState}
        </FormLabel>
        <Dropdown
          id="jobsite-information-dropdown"
          disabled={!dropdownOptions[0]}
          value={getDropdownValue()}
          onChange={(event) => { handleDropdownSelection(event); }}
          placeholder={placeholderValue || dropdownOptions[0] || ''}
        >
          {(dropdownOptions[0]) && (
            dropdownOptions.map((location, index) => (
              <MenuItem key={dropdownOptions[index] || ''} value={location}>{location}</MenuItem>
            ))
          )}
        </Dropdown>
      </FormController>
      {isJobsiteInformationEnabled && zipCodeField}
    </div>
  );
};

JobsiteInformationForm.displayName = 'JobsiteInformationForm';

JobsiteInformationForm.propTypes = {
  showErrors: bool.isRequired,
  jobsiteInformation: shape({
    addressLine1: string,
    addressLine2: string,
    city: string,
    state: string,
    zipCode: string,
    country: string
  }).isRequired,
  setJobsiteInformation: func.isRequired,
  isJobsiteError: bool.isRequired,
  setIsJobsiteError: func.isRequired,
  open: bool.isRequired,
  isZipOnlyEnabled: bool,
  /** The DSCVR-5168 create-drawer layout and zip handling. Only the create drawer passes it. */
  isJobsiteInformationEnabled: bool
};

JobsiteInformationForm.defaultProps = {
  // Defaults on so the other consumer of this form - ProjectDetailsForm - keeps the behavior it
  // has today. Only the create drawer passes this, driven by fs:isJobsiteInformationEnabled.
  isZipOnlyEnabled: true,
  isJobsiteInformationEnabled: false
};

JobsiteInformationForm.dataModel = extend({
  cityStateLookupByZipCode: params({
    zipCode: stringType().isRequired()
  }).shape({
    postalDetailsList: arrayOf(
      shapeType({
        city: stringType(),
        state: stringType()
      })
    )
  })
});